/**
 * 44. Credit Limit Review — pending credit decisions; customer risk;
 * approve/decline.
 *
 * Arabic and English (C7): every word from the catalogs; a risk score and a
 * decision's status are words, never the stored value.
 */
import { Card, PageHeader, DataTable, Skeleton, Badge } from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { statusLabel, valueLabel } from "@/lib/i18n/records";
import { formatMoney } from "@/lib/money";

interface CreditDecisionRow {
  id: string;
  customerName: string;
  requestedLimit: string;
  currentLimit: string;
  openAr: string;
  riskScore: "Low" | "Medium" | "High";
  requestedBy: string;
  currencyCode: string;
  status: "Pending" | "Approved" | "Declined";
}

export function CreditLimitReviewPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<CreditDecisionRow>(
    ["sales", "credit-limit-review"],
    "/sales/credit-limit-review",
  );

  return (
    <div>
      <PageHeader
        title={t("sales.creditReview.title")}
        subtitle={t("sales.creditReview.subtitle")}
        actions={
          <PendingButton
            variant="primary"
            reason="creditApproval"
            slice="CRM-B15"
          >
            {t("sales.creditReview.approveSelected")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("sales.creditReview.list"), data.total)}>
          <DataTable<CreditDecisionRow>
            rows={data.items}
            columns={[
              { key: "customerName", label: t("fields.customer") },
              {
                key: "currentLimit",
                label: t("sales.creditReview.currentLimit"),
                numeric: true,
                render: (r) => formatMoney(r.currentLimit, r.currencyCode),
              },
              {
                key: "requestedLimit",
                label: t("sales.creditReview.requestedLimit"),
                numeric: true,
                render: (r) => formatMoney(r.requestedLimit, r.currencyCode),
              },
              {
                key: "openAr",
                label: t("sales.creditReview.openAr"),
                numeric: true,
                render: (r) => formatMoney(r.openAr, r.currencyCode),
              },
              {
                key: "riskScore",
                label: t("fields.risk"),
                render: (r) => (
                  <Badge
                    tone={
                      r.riskScore === "High"
                        ? "danger"
                        : r.riskScore === "Medium"
                          ? "warning"
                          : "success"
                    }
                  >
                    {valueLabel(t, "risk", r.riskScore)}
                  </Badge>
                ),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <Badge
                    tone={
                      r.status === "Approved"
                        ? "success"
                        : r.status === "Declined"
                          ? "danger"
                          : "warning"
                    }
                  >
                    {statusLabel(t, "CreditLimitRequest", r.status)}
                  </Badge>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
